import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Volume2, VolumeX, Zap, RotateCcw } from "lucide-react";
import { LogoRea, LogoPartners } from "@/components/Logos";

function buildSources(url) {
  if (!url) return [];
  const m = url.match(/(.*\/upload\/)(?:[^/]*\/)?(v\d+\/.*?)(?:\.[a-z0-9]+)?$/i);
  if (url.includes("res.cloudinary.com") && m) {
    const base = m[1];
    const rest = m[2];
    return [
      { src: `${base}f_webm,vc_vp9,q_auto/${rest}.webm`, type: "video/webm" },
      { src: `${base}f_mp4,vc_h264,q_auto/${rest}.mp4`, type: "video/mp4" },
    ];
  }
  return [{ src: url }];
}

export default function VideoGate({ settings, onComplete }) {
  const videoRef = useRef(null);
  const [ended, setEnded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(true);
  const [flash, setFlash] = useState(false);
  const sources = buildSources(settings?.video_url);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      if (v.duration) setProgress((v.currentTime / v.duration) * 100);
    };
    const onEnded = () => {
      setEnded(true);
      setFlash(true);
      setProgress(100);
      setTimeout(() => setFlash(false), 700);
    };
    const onError = () => setEnded(true);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("ended", onEnded);
    v.addEventListener("error", onError);
    const play = v.play();
    if (play && play.catch) play.catch(() => {});
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("error", onError);
    };
  }, []);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted && v.paused) v.play().catch(() => {});
  };

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    setEnded(false);
    setProgress(0);
    v.currentTime = 0;
    v.play().catch(() => {});
  };

  const handleContinue = () => {
    if (!ended) return;
    sessionStorage.setItem("video_seen", "1");
    onComplete();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center overflow-y-auto bg-[#050505] px-4 py-6"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.04, filter: "blur(8px)" }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
      data-testid="video-gate"
    >
      <div className="glow-underlay" />
      {/* Logos */}
      <div className="mb-6 flex w-full max-w-4xl items-center justify-between">
        <LogoPartners src={settings?.logo_partners} size="h-11 md:h-14" />
        <LogoRea src={settings?.logo_rea} size="h-16 md:h-20" />
      </div>

      {/* Title */}
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-4 text-center font-display text-2xl font-extrabold tracking-tight sm:text-3xl md:text-4xl"
      >
        Preparati a vivere la <span className="neon-gradient-text">Convention</span>
      </motion.p>

      {/* Video (vertical / TikTok 9:16) */}
      <div className="relative mx-auto flex w-full flex-col items-center">
        {flash && (
          <motion.div
            className="pointer-events-none absolute inset-0 z-20 rounded-2xl bg-white"
            initial={{ opacity: 0.7 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
          />
        )}
        <div className="relative aspect-[9/16] h-[56vh] max-h-[600px] w-auto overflow-hidden rounded-2xl border border-white/10 shadow-[0_0_60px_rgba(230, 0, 0,0.25)]">
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            autoPlay
            muted
            playsInline
            data-testid="intro-video"
          >
            {sources.map((s) => (
              <source key={s.src} src={s.src} type={s.type} />
            ))}
          </video>
          <button
            onClick={toggleSound}
            className="absolute bottom-3 right-3 z-10 grid h-10 w-10 place-items-center rounded-full bg-black/60 text-white backdrop-blur transition-colors hover:bg-black/80"
            aria-label={muted ? "Attiva audio" : "Disattiva audio"}
            data-testid="sound-toggle"
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        {/* Progress indicator */}
        <div className="mt-3 h-1.5 w-56 overflow-hidden rounded-full bg-white/10 sm:w-64" data-testid="video-progress">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#f5c542] via-[#ff7a1a] to-[#e60000] transition-[width] duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Button */}
      <div className="mt-7 flex flex-col items-center gap-3">
        <button
          onClick={handleContinue}
          disabled={!ended}
          data-testid="gate-continue-btn"
          className={`group flex items-center gap-3 rounded-full px-8 py-4 font-display text-sm font-extrabold uppercase tracking-[0.15em] transition-all sm:text-base ${
            ended ? "btn-neon active animate-pulse-glow" : "btn-locked"
          }`}
        >
          {ended ? (
            <>
              <Zap size={20} className="transition-transform group-hover:scale-125" />
              Conferma la tua presenza
            </>
          ) : (
            <>
              <Lock size={18} className="animate-lock-pulse" />
              Guarda il video per continuare
            </>
          )}
        </button>

        {ended && (
          <button
            onClick={replay}
            className="flex items-center gap-2 text-xs text-white/50 transition-colors hover:text-white"
            data-testid="gate-replay-btn"
          >
            <RotateCcw size={14} /> Rivedi il video
          </button>
        )}
      </div>
    </motion.div>
  );
}
